import type { ChartVariant } from '../chart/types.ts';
import { DEFAULT_FIT_CONFIG, fitLoop, type FitConfig, type FitResult } from './fitLoop.ts';
import { renderChart } from './chart.ts';
import { miesciSie, miesciSieNaWysokosc } from './viewport.ts';

// Polityka wyboru między „jeden wiersz akordów" a „kotwice tekstowe".
//
// Bierze się z konkretnego zrzutu z telefonu MH: wiersz akordów zawijał się w
// połowie, a druga linijka zaczynała się sierotą `Am Em Am`, przez co granica
// frazy czytała się w złym miejscu. Poziom 2 degradacji obiecuje frazy w
// JEDNYM wierszu, więc zawinięcie łamało to, co wariant deklaruje.
//
// Samo dołożenie zakazu zawijania do pomiaru nie jest jednak rozwiązaniem i to
// jest wynik pomiaru, nie ostrożność: przy twardym zakazie trzy z dziewięciu
// utworów korpusu spadały na poziom 7 do trybu awaryjnego i traciły WSZYSTKIE
// kotwice. Plan rozstrzyga ten spór wprost i na korzyść kotwic, bo po
// pierwszych słowach poznaje się, którą zwrotkę ma się przed sobą, a po
// nieprzełamanym wierszu akordów nie poznaje się niczego.
//
// Stąd dwa przebiegi i wybór między nimi:
//
// 1. OSTRY, z zakazem zawijania. Wiersz akordów musi zmieścić się w szerokości
//    ekranu albo skrót schodzi o stopień pisma lub o poziom degradacji.
// 2. ŁAGODNY, bez wymiaru poziomego. Zawinięcie jest dozwolone, ale wyłącznie
//    na granicy frazy (frazy renderują się jako nierozrywalne całości), a
//    kontynuacja dostaje wcięcie wiszące, więc widać, że to dalszy ciąg
//    wiersza, a nie nowa fraza.
//
// Bierzemy ostry, DOPÓKI nie kosztuje. Kosztuje wtedy, gdy wchodzi w tryb inny
// niż `ok` albo gdy odbiera kotwice, które przebieg łagodny zachowuje.

/** O ile większe pismo musi dać łagodny przebieg, żeby wygrać z ostrym, który
 * nic nie kosztuje. Poniżej tego zysk nie jest wart zawinięcia wiersza. */
const PROG_WIEKSZEGO_PISMA = 1.25;

export interface WynikDopasowania {
  wynik: FitResult;
  wariant: ChartVariant;
  /** Czy render ma zakazywać zawijania wiersza akordów. */
  bezZawijania: boolean;
}

function maKotwice(wariant: ChartVariant): boolean {
  return wariant.sections.some((s) => s.anchor !== null);
}

/**
 * Mierzy oba przebiegi i zwraca ten, który daje lepszy skrót. Renderuje przy
 * okazji do `kontener`, więc po powrocie kontener zawiera render zwycięskiego
 * wariantu i nie trzeba go rysować drugi raz.
 *
 * Współdzielone przez aplikację i harness testowy CELOWO. Rozjazd między nimi
 * jest tym, co raz już ukryło błąd wysyłający aplikację na telefon w trybie
 * awaryjnym, więc polityka wyboru mieszka w jednym miejscu, a nie w dwóch.
 */
export function dopasuj(
  kontener: HTMLElement,
  warianty: readonly ChartVariant[],
  opcje: {
    config?: FitConfig;
    showDroppedInfo?: boolean;
    /** Patrz `RenderOptions.wierszMetaZawsze` (`chart.ts`): przekazywane
     * niezmienione do KAŻDEGO wywołania `renderChart`, i pomiarowego, i
     * finalnego, żeby zmierzony DOM był dokładnie tym, co zobaczy człowiek. */
    wierszMetaZawsze?: boolean;
    strojInfo?: string | null;
  } = {},
): WynikDopasowania {
  const config = opcje.config ?? DEFAULT_FIT_CONFIG;
  const showDroppedInfo = opcje.showDroppedInfo === true;
  const wierszMetaZawsze = opcje.wierszMetaZawsze === true;
  const strojInfo = opcje.strojInfo ?? null;

  const przebieg = (bezZawijania: boolean): WynikDopasowania => {
    const wynik = fitLoop(
      warianty,
      (wariant, fontSizePx) => {
        // Znacznik „skrót skrócony" jest w pomiarze ZAWSZE, choć render
        // finalny pokaże go tylko przy statusie zdegradowanym. Pomiar
        // rezerwuje najgorszy przypadek, więc render nie może wyjść wyższy
        // od zmierzonego. Odwrotna kolejność była tu już błędem.
        renderChart(kontener, wariant, {
          fontSizePx,
          showDroppedInfo,
          showTruncatedBadge: true,
          nowrapChords: bezZawijania,
          wierszMetaZawsze,
          strojInfo,
        });
        return bezZawijania ? miesciSie(kontener) : miesciSieNaWysokosc(kontener);
      },
      config,
    );
    return { wynik, wariant: warianty[wynik.level] as ChartVariant, bezZawijania };
  };

  const ostry = przebieg(true);
  const lagodny = przebieg(false);

  // Trzeci koszt ostrego przebiegu wyszedł na szerokim ekranie komputera:
  // najdłuższy wiersz akordów (13 fraz zwrotki „Jolki”) wyznaczał pismo
  // 28 px dla całego utworu, a pół wysokości ekranu stało puste. Gdy łagodny
  // przebieg daje wyraźnie większe pismo, bez gorszego poziomu i bez utraty
  // kotwic, zawinięcie na granicy frazy jest tańsze od drobnych akordów.
  const lagodnyWiekszy = lagodny.wynik.status === 'ok'
    && lagodny.wynik.level <= ostry.wynik.level
    && (maKotwice(lagodny.wariant) || !maKotwice(ostry.wariant))
    && (lagodny.wynik.fontSizePx >= ostry.wynik.fontSizePx * PROG_WIEKSZEGO_PISMA
      // Bogatszy poziom przy co najmniej tym samym piśmie też wygrywa: ostry
      // płaci wtedy degradacją, czyli treścią, a nie tylko pismem.
      || (lagodny.wynik.level < ostry.wynik.level && lagodny.wynik.fontSizePx >= ostry.wynik.fontSizePx));
  const ostryPlaci = ostry.wynik.status !== 'ok'
    || (!maKotwice(ostry.wariant) && maKotwice(lagodny.wariant))
    || lagodnyWiekszy;
  const wybrany = ostryPlaci ? lagodny : ostry;

  renderChart(kontener, wybrany.wariant, {
    fontSizePx: wybrany.wynik.fontSizePx,
    showDroppedInfo,
    showTruncatedBadge: wybrany.wynik.status === 'zdegradowany',
    nowrapChords: wybrany.bezZawijania,
    wierszMetaZawsze,
    strojInfo,
  });

  return wybrany;
}
